/**
 * One answer to "what is this deal's address", for every surface that prints it.
 *
 * The address lives in three places that never agreed on who wins: the deal's
 * own street / city / state / ZIP, the property master, and what Google Places
 * resolved from the building's name. The header read the deal first, another
 * panel read the master first, the map pinned the master's old coordinate —
 * so a street a person corrected showed in one place and not the others.
 *
 * The order, strongest first:
 *
 *     person-entered  >  package-stated  >  master
 *
 * Google Places is no rung on that ladder. It once resolved a street from the
 * building's NAME and the result was stored as if a document had stated it;
 * "Washington Square" became the Washington Monument. Rows written that way
 * still exist, so they are recognised and WITHHELD — left out of selection, not
 * demoted below the master — and the part is reported in `withheld` so the page
 * can say a value was set aside rather than that none was ever stated.
 *
 * This is the browser's twin of `backend/app/services/address_precedence.py`
 * (the printed dossier and the dossier context run the backend one); both are
 * held to one generated fixture (`addressPrecedence.parity.json`, checked in
 * `addressPrecedence.parity.test.ts`). Parts are chosen one at a time — state,
 * then city, then street and ZIP — and a street or ZIP is only borrowed from a
 * source whose own city and state agree with the ones chosen, so a street from
 * Norcross is never stitched under a city a person set to Atlanta.
 */
import type { DossierAddress, DossierPropertyMaster } from '@/services/dossierContext'

/** `places` survives only to recognise rows written before name lookups were
 *  retired; nothing selects it. */
export type AddressSource = 'person' | 'package' | 'places' | 'master'
/** Strongest first. A source not listed here is not selectable. */
export const ADDRESS_ORDER: readonly AddressSource[] = ['person', 'package', 'master']

export type AddressPart = 'street' | 'city' | 'state' | 'zip'
export type AddressParts = Partial<Record<AddressPart, string | null | undefined>>
export type AddressSources = Partial<Record<AddressPart, AddressSource>>

export type ResolvedAddress = {
  street: string | null
  city: string | null
  state: string | null
  zip: string | null
  /** Composed "street, city, ST zip" — or the location text when no part states a place. */
  full: string
  source: AddressSource | null
  partSources: AddressSources
  /** Parts a name lookup wrote, set aside rather than shown. */
  withheld: AddressPart[]
  /** The `location` text itself was a name lookup's and is not used for `full`. */
  withheldLocation: boolean
}

function clean(value: unknown): string | null {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim()
  return text || null
}

// The server folds case with Python's `casefold`, which reads "ß" as "ss"; a bare
// `toLowerCase` kept it and the strip below then dropped it, so "Straße" and
// "STRASSE" matched on one side and not the other.
function norm(value: unknown): string {
  return (clean(value) ?? '').toLowerCase().replace(/ß/g, 'ss').replace(/[^a-z0-9 ]/g, '')
}

const PARTS: readonly AddressPart[] = ['street', 'city', 'state', 'zip']

function agrees(home: [string | null, string | null], city: string | null, state: string | null): boolean {
  const [homeCity, homeState] = home
  if (state && homeState && norm(homeState) !== norm(state)) return false
  if (city && homeCity && norm(homeCity) !== norm(city)) return false
  return true
}

export type AddressInput = {
  deal: AddressParts
  /** Where each of the deal's parts came from; `package` when absent. */
  dealSources?: AddressSources | null
  master?: AddressParts | null
  /** How the master came to hold its address; `master` when absent. */
  masterRank?: AddressSource | null
  /** Which parts of a `places` master the lookup wrote; absent: all of them. */
  masterPlacesParts?: readonly AddressPart[] | null
  /** The deal's `location` text, shown only when no part states a place. */
  location?: string | null
  locationSource?: AddressSource | null
}

export function resolveAddress(input: AddressInput): ResolvedAddress {
  const groups = {
    deal: Object.fromEntries(PARTS.map((p) => [p, clean(input.deal[p])])) as Record<AddressPart, string | null>,
    master: Object.fromEntries(PARTS.map((p) => [p, clean(input.master?.[p])])) as Record<AddressPart, string | null>,
  }
  const lookupWrote = input.masterPlacesParts ?? null
  const masterRankOfPart = (part: AddressPart): AddressSource => {
    const rank = input.masterRank ?? 'master'
    if (rank !== 'places') return rank
    // A Places master's other parts are the master's own, not the lookup's.
    return lookupWrote === null || lookupWrote.includes(part) ? 'places' : 'master'
  }
  const rankOf = (group: 'deal' | 'master', part: AddressPart): AddressSource =>
    group === 'deal' ? input.dealSources?.[part] ?? 'package' : masterRankOfPart(part)

  // A value a name lookup wrote is withheld BEFORE anything is chosen: blanked
  // out of its group, so it neither wins a part nor lends its city and state to
  // the agreement check of the parts around it. (Filtering candidates only left
  // a Places-written "Iconic" free to veto a person's own street.)
  const guessed: Partial<Record<AddressPart, string[]>> = {}
  for (const group of ['deal', 'master'] as const) {
    for (const part of PARTS) {
      const value = groups[group][part]
      if (value && rankOf(group, part) === 'places') {
        guessed[part] = [...(guessed[part] ?? []), value]
        groups[group][part] = null
      }
    }
  }
  const homes = {
    deal: [groups.deal.city, groups.deal.state] as [string | null, string | null],
    master: [groups.master.city, groups.master.state] as [string | null, string | null],
  }

  const chosen: Record<AddressPart, string | null> = { street: null, city: null, state: null, zip: null }
  const chosenSource: AddressSources = {}

  const pick = (part: AddressPart, city: string | null = null, state: string | null = null) => {
    const found = (['deal', 'master'] as const)
      .map((group, index) => ({ group, index, value: groups[group][part] }))
      .filter((c) => c.value)
      // A source this order does not name ranks last, as on the server.
      .map((c) => {
        const rank = ADDRESS_ORDER.indexOf(rankOf(c.group, part))
        return { ...c, rank: rank >= 0 ? rank : ADDRESS_ORDER.length }
      })
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
    for (const candidate of found) {
      // The state is chosen first and constrains nothing; every later part must
      // come from a source that agrees with what is already chosen.
      if (part !== 'state' && !agrees(homes[candidate.group], city, state)) continue
      chosen[part] = candidate.value
      chosenSource[part] = rankOf(candidate.group, part)
      return
    }
  }

  pick('state')
  pick('city', null, chosen.state)
  pick('street', chosen.city, chosen.state)
  pick('zip', chosen.city, chosen.state)

  // `loans.location` is recomposed from the parts, so a lookup that wrote the
  // parts wrote this text too.
  const locationSource = input.locationSource ?? 'package'
  const withheldLocation = Boolean(clean(input.location)) && locationSource === 'places'
  const location = withheldLocation ? null : clean(input.location)
  const stated =
    (['street', 'city', 'state'] as const).map((p) => (chosen[p] ? chosenSource[p] : undefined)).find(Boolean)
    ?? (location ? locationSource : undefined)
  const tail = [chosen.state, chosen.zip].filter(Boolean).join(' ')
  const composed = [chosen.street, chosen.city, tail].filter(Boolean).join(', ')
  const full = !chosen.street && !chosen.city && location ? location : composed || location || ''
  return {
    street: chosen.street,
    city: chosen.city,
    state: chosen.state,
    zip: chosen.zip,
    full,
    source: stated ?? null,
    partSources: Object.fromEntries(PARTS.filter((p) => chosen[p]).map((p) => [p, chosenSource[p]])) as AddressSources,
    // Judged after the choice, as on the server: every part a name lookup wrote a
    // value for that is not the one shown. The same text arriving from a source
    // that is allowed to state it is not hidden.
    withheld: PARTS.filter((p) => (guessed[p] ?? []).some((value) => norm(value) !== norm(chosen[p]))),
    withheldLocation,
  }
}

// ── the property master as an address source ────────────────────────────────

/** The master's address parts, as the precedence reads them. */
export function masterParts(master: DossierPropertyMaster | null | undefined): AddressParts | null {
  if (!master) return null
  return { street: master.address, city: master.city, state: master.state, zip: master.zipCode }
}

/** How the master came to hold its address — recorded on its enrichment. */
export function masterRankOf(master: DossierPropertyMaster | null | undefined): AddressSource {
  const enrichment = (master?.enrichment ?? {}) as Record<string, unknown>
  const recorded = enrichment.addressSource as { source?: string } | undefined
  if (recorded?.source === 'manual') return 'person'
  const resolution = enrichment.addressResolution as { street?: string } | undefined
  if (resolution && clean(master?.address) && norm(resolution.street) === norm(master?.address)) return 'places'
  return 'master'
}

function zip5(value: unknown): string {
  return /^\s*(\d{5})/.exec(String(value ?? ''))?.[1] ?? ''
}

/**
 * Which parts of a Places-ranked master the retired resolver wrote: the street
 * always, and the city / state / ZIP when they equal what `addressResolution`
 * recorded it saw - or when the record says nothing about that part (unknown is
 * withheld, never shown). The twin of `address_precedence.master_places_parts`.
 */
export function masterPlacesPartsOf(master: DossierPropertyMaster | null | undefined): AddressPart[] {
  if (masterRankOf(master) !== 'places') return []
  const resolution = ((master?.enrichment ?? {}) as Record<string, unknown>).addressResolution as
    Record<string, unknown>
  const found: AddressPart[] = ['street']
  const columns: [AddressPart, unknown, string][] = [
    ['city', master?.city, 'city'],
    ['state', master?.state, 'state'],
    ['zip', master?.zipCode, 'zipCode'],
  ]
  for (const [part, ours, key] of columns) {
    if (!(key in resolution)) found.push(part)
    else if (part === 'zip' ? zip5(ours) !== '' && zip5(ours) === zip5(resolution[key])
                            : norm(ours) !== '' && norm(ours) === norm(resolution[key])) found.push(part)
  }
  return found
}

/**
 * The address every surface shows: the deal's own parts (as read from the deal
 * file, so an edit just saved is reflected before the server block refetches),
 * the master, and the server's account of where each came from.
 */
export function displayedAddress(input: {
  deal: AddressParts
  location?: string | null
  master?: DossierPropertyMaster | null
  /** The server's resolved block, which names the provenance of each input. */
  server?: DossierAddress | null
}): ResolvedAddress {
  const server = input.server as (DossierAddress & { masterPlacesParts?: AddressPart[] }) | null | undefined
  return resolveAddress({
    deal: input.deal,
    dealSources: server?.dealSources ?? null,
    master: masterParts(input.master),
    masterRank: server?.masterRank ?? masterRankOf(input.master),
    masterPlacesParts: server?.masterPlacesParts ?? masterPlacesPartsOf(input.master),
    location: input.location,
    locationSource: server?.locationSource ?? null,
  })
}

/** Are these two street lines the same street, ignoring case and punctuation? */
export function streetsMatch(a: unknown, b: unknown): boolean {
  const left = norm(a)
  return left !== '' && left === norm(b)
}

/** Are these two place names the same, ignoring case and punctuation? */
export function placesMatch(a: unknown, b: unknown): boolean {
  return norm(a) !== '' && norm(a) === norm(b)
}

/** How precisely the master's coordinate was placed, and by whom. */
export function masterGeocode(
  master: DossierPropertyMaster | null | undefined,
): { precision: string | null; source: string | null } {
  const geocode = ((master?.enrichment ?? {}) as Record<string, unknown>).geocode as
    | { precision?: unknown; source?: unknown }
    | undefined
  return {
    precision: typeof geocode?.precision === 'string' ? geocode.precision : null,
    source: typeof geocode?.source === 'string' ? geocode.source : null,
  }
}

/**
 * May a point carrying this `enrichment.geocode` stamp be drawn as the asset?
 * The twin of `address_precedence.stamped_point_applies`, rule for rule:
 *
 *  - no record (or an empty one) predates provenance: taken on the street match;
 *  - `google-places` (chosen from a NAME): never;
 *  - `manual` (a person placed it): always;
 *  - `intake` (copied off the deal): rooftop, street, or an unstated precision
 *    (a hand-entered coordinate) - never a city-level one;
 *  - anything else (a geocoder's, or the source-less reset record an address
 *    edit leaves): rooftop or street precision only.
 */
export function stampedPointApplies(geocode: unknown): boolean {
  if (typeof geocode !== 'object' || geocode === null || Array.isArray(geocode)) return true
  const stamp = geocode as { source?: unknown; precision?: unknown }
  if (Object.keys(geocode).length === 0) return true
  const { source, precision } = stamp
  if (source === 'google-places') return false
  if (source === 'manual') return true
  if (source === 'intake') return precision === 'rooftop' || precision === 'street' || precision === 'unstated'
  return precision === 'rooftop' || precision === 'street'
}

/**
 * Is the master's coordinate a point on THIS address? A coordinate is only as
 * good as the address it was resolved for, and only as good as the lookup that
 * placed it: the point must sit on the street shown, in the city, state and ZIP
 * shown (where both sides state them - "100 Main St" exists in every state), not
 * on a master a name lookup wrote, and carry a stamp that allows it
 * (`stampedPointApplies`). With no street shown there is nothing for a point to
 * be on.
 *
 * The twin of `address_precedence.master_point_applies`, held to it by
 * `addressPrecedence.parity.json`. A caller that passes only the street skips
 * the city / state / ZIP agreement; the server's verdict
 * (`masterPointApplies: false`) overrides this wherever it speaks.
 */
export function masterPointApplies(
  resolved: Pick<ResolvedAddress, 'street'> & Partial<Pick<ResolvedAddress, 'city' | 'state' | 'zip'>>,
  master: DossierPropertyMaster | null | undefined,
): boolean {
  if (!master || typeof master.lat !== 'number' || typeof master.lng !== 'number') return false
  if (!resolved.street) return false
  if (norm(resolved.street) !== norm(master.address)) return false
  if (!agrees([master.city, master.state], resolved.city ?? null, resolved.state ?? null)) return false
  const [masterZip, shownZip] = [zip5(master.zipCode), zip5(resolved.zip)]
  if (masterZip && shownZip && masterZip !== shownZip) return false
  if (masterRankOf(master) === 'places') return false
  return stampedPointApplies(((master.enrichment ?? {}) as Record<string, unknown>).geocode)
}
